export interface CartillaPlan {
  CARTILLA: string;
  CRT_CODIGO: string;
  PLAN_VENTA: string;
  REFERENCIAS: string;
  displayName?: string;
}

/** Deploy interno/VPN: true. Deploy público: omitir o false. */
export function showAllPlans(): boolean {
  return process.env.NEXT_PUBLIC_SHOW_ALL_PLANS === 'true';
}

export const ALLOWED_MEDICAL_PLANS = [
  'AZUL',
  'CELESTE',
  'FAMILY',
  'INTEGRA 2',
  'CONECTA',
  'MINISTERIO DE SEGURIDAD 1',
  'PLAN MINISTERIO DE SEGURIDAD 2',
] as const;

export const ALLOWED_ODONT_PLANS = [
  'ODONTOLOGIA AZUL',
  'ODONTOLOGIA CELESTE',
  'ODONTOLOGIA FAMILY',
  'ODONTOLOGIA INTEGRA 2',
  'ODONTOLOGIA CONECTA',
  'ODONTOLOGIA MS 1',
  'PLAN MINISTERIO DE SEGURIDAD 2',
] as const;

export const MEDICAL_PLAN_DISPLAY_NAMES: Record<string, string> = {
  'MINISTERIO DE SEGURIDAD 1': 'MS1',
  'PLAN MINISTERIO DE SEGURIDAD 2': 'MS2',
};

export const ODONT_PLAN_DISPLAY_NAMES: Record<string, string> = {
  'ODONTOLOGIA MS 1': 'MS1',
  'PLAN MINISTERIO DE SEGURIDAD 2': 'MS2',
};

/**
 * True si el plan (por su código CARTILLA) es del Ministerio de Seguridad
 * (MS1 o MS2). Los mapas de display names contienen exactamente esos planes.
 */
export function isMinisterioSeguridadPlan(cartilla: string | undefined | null): boolean {
  if (!cartilla) return false;
  return cartilla in MEDICAL_PLAN_DISPLAY_NAMES || cartilla in ODONT_PLAN_DISPLAY_NAMES;
}

export function filterMedicalPlans(plans: CartillaPlan[]): CartillaPlan[] {
  if (showAllPlans()) return plans;

  return plans.filter(
    (plan) => plan.PLAN_VENTA === 'V' && ALLOWED_MEDICAL_PLANS.includes(plan.CARTILLA as (typeof ALLOWED_MEDICAL_PLANS)[number])
  );
}

export function filterOdontPlans(plans: CartillaPlan[]): CartillaPlan[] {
  if (showAllPlans()) return plans;

  return plans.filter((plan) =>
    ALLOWED_ODONT_PLANS.includes(plan.CARTILLA as (typeof ALLOWED_ODONT_PLANS)[number])
  );
}

export function withMedicalDisplayNames(plans: CartillaPlan[]): CartillaPlan[] {
  return plans.map((plan) => ({
    ...plan,
    displayName: MEDICAL_PLAN_DISPLAY_NAMES[plan.CARTILLA],
  }));
}

export function formatOdontPlansForDisplay(plans: CartillaPlan[]): CartillaPlan[] {
  const seen = new Set<string>();

  return plans
    .filter((plan) => !seen.has(plan.CARTILLA) && seen.add(plan.CARTILLA))
    .map((plan) => ({
      ...plan,
      displayName:
        ODONT_PLAN_DISPLAY_NAMES[plan.CARTILLA] ??
        plan.CARTILLA.replace(/^ODONTOLOGIA\s+/i, ''),
    }))
    .sort((a, b) => {
      const aIsConecta = a.CARTILLA.includes('CONECTA');
      const bIsConecta = b.CARTILLA.includes('CONECTA');

      if (aIsConecta && !bIsConecta) return 1;
      if (!aIsConecta && bIsConecta) return -1;
      return 0;
    });
}

/** True si el código CARTILLA corresponde a un plan Conecta (médico u odontológico). */
export function isConectaPlan(cartilla: string | undefined | null): boolean {
  return !!cartilla && cartilla.includes('CONECTA');
}

/**
 * True si una localidad (PROVINCIA/ZONA) es de CABA. El backend de
 * /Locations/PlanWeb/{plan} NO restringe estrictamente a CABA para Conecta
 * (localidades de GBA linderas, ej. Florida/Vicente López, zona Z3, también
 * matchean), así que esta validación se hace en el cliente (VEN-395).
 */
export function isCabaLocation(loc: { PROVINCIA?: string; ZONA?: string } | null | undefined): boolean {
  if (!loc) return false;
  const provincia = loc.PROVINCIA?.trim().toUpperCase();
  const zona = loc.ZONA?.trim().toUpperCase();
  return provincia === 'CAPITAL FEDERAL' || zona === 'Z1';
}

export function sortPlansConectaLast(plans: CartillaPlan[]): CartillaPlan[] {
  return [...plans].sort((a, b) => {
    const aIsConecta = a.CARTILLA.includes('CONECTA');
    const bIsConecta = b.CARTILLA.includes('CONECTA');

    if (aIsConecta && !bIsConecta) return 1;
    if (!aIsConecta && bIsConecta) return -1;
    return 0;
  });
}
